import React from 'react';
import { FaGooglePlay, FaApple } from 'react-icons/fa';
import brandLogo from '../website-header/brand-logo.png';
import dunsSeal from './assets/duns-registered-seal-sm.png';
import dunsQr from './assets/duns-profile-qr-sm.png';
import { FOOTER_COLUMNS } from '../website-header/navData';
import useNavTo from '../website-header/useNavTo';
import { useConsent } from '../../privacy/ConsentContext';
import {
  LEGAL_ENTITY_NAME,
  GST_NUMBER,
  DUNS_NUMBER,
  DUNS_PROFILE_URL,
  PLAY_STORE_URL,
  PLAY_STORE_AVAILABLE,
  APP_STORE_URL,
  APP_STORE_AVAILABLE,
} from '../../constants/constants';
import './UnAuthWebsiteFooter.css';

const STORE_BADGES = [
  { key: 'play', Icon: FaGooglePlay, name: 'Google Play', url: PLAY_STORE_URL, available: PLAY_STORE_AVAILABLE },
  { key: 'apple', Icon: FaApple, name: 'App Store', url: APP_STORE_URL, available: APP_STORE_AVAILABLE },
];

const StoreBadge = ({ Icon, name, url, available }) => {
  const content = (
    <>
      <Icon className="rl-store-icon" aria-hidden="true" />
      <span className="rl-store-text">
        <small>{available ? 'Get it on' : 'Coming soon to'}</small>
        {name}
      </span>
    </>
  );

  // No live listing yet — render the badge, but don't link anywhere.
  if (!available) {
    return (
      <span className="rl-store-badge is-soon" aria-disabled="true" title={`Raya ERP is not on ${name} yet`}>
        {content}
      </span>
    );
  }

  return (
    <a className="rl-store-badge" href={url} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  );
};

export const UnAuthWebsiteFooter = () => {
  const navTo = useNavTo();
  const { openPreferences } = useConsent();

  return (
    <footer className="rl-footer">
      <div className="rl-footer-inner">
        <div className="rl-footer-grid">
          <div>
            <div className="rl-footer-brand">
              <img src={brandLogo} alt="Raya" className="rl-footer-logo" width="69" height="26" loading="lazy" />
              <span className="rl-footer-brand-erp">ERP</span>
            </div>
            <p className="rl-footer-blurb">
              One platform for inventory, sales, point of sale, manufacturing, finance and HR. Built for Bharat.
            </p>

            <div className="rl-footer-apps">
              {STORE_BADGES.map(({ key, ...badge }) => (
                <StoreBadge key={key} {...badge} />
              ))}
            </div>
          </div>

          {/* Link columns come from navData.js, the same source as the header. */}
          {FOOTER_COLUMNS.map((col) => (
            <div className="rl-footer-col" key={col.title}>
              <span className="rl-footer-coltitle">{col.title}</span>
              {col.links.map((l) => (l.action === 'privacy-choices' ? (
                <button key={l.label} type="button" className="rl-footer-link" onClick={openPreferences}>{l.label}</button>
              ) : (
                <a key={l.label} className="rl-footer-link" href={l.href} onClick={(e) => navTo(l.href, e)}>{l.label}</a>
              )))}
            </div>
          ))}
        </div>

        <div className="rl-footer-compliance">
          <a
            className="rl-duns-seal"
            href={DUNS_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            title="View the online D-U-N-S® Registered™ profile for Raya Solution &amp; Company"
          >
            <img src={dunsSeal} alt="Dun &amp; Bradstreet D-U-N-S Registered" width="66" height="62" loading="lazy" />
          </a>

          <div className="rl-compliance-detail">
            <span className="rl-footer-coltitle">Verified business</span>
            <p className="rl-compliance-entity">{LEGAL_ENTITY_NAME}</p>
            <dl className="rl-compliance-ids">
              <div>
                <dt>D-U-N-S&reg; No.</dt>
                <dd>{DUNS_NUMBER}</dd>
              </div>
              <div>
                <dt>GSTIN</dt>
                <dd>{GST_NUMBER}</dd>
              </div>
            </dl>
            <a
              className="rl-compliance-link"
              href={DUNS_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              View the D-U-N-S&reg; Registered&trade; profile &rarr;
            </a>
          </div>

          <a
            className="rl-duns-qr"
            href={DUNS_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            title="Scan to open the D-U-N-S® Registered™ profile"
          >
            <img src={dunsQr} alt="QR code linking to the D-U-N-S Registered profile" width="66" height="66" loading="lazy" />
          </a>
        </div>

        <div className="rl-footer-bottom">
          <span>&copy; {new Date().getFullYear()} Raya Software Solutions</span>
          <span>Raya ERP · Est. Ahmedabad · India</span>
        </div>
      </div>
    </footer>
  );
};
